import { z } from "zod";

/**
 * Environment configuration
 * * SECURITY NOTE:
 * Uses Zod for strict runtime validation. This ensures the application
 * "Fails Fast" on startup if critical configuration (like API URLs)
 * is missing or malformed, rather than failing silently during user interaction.
 */

// 1. Define allowed environments
const AppEnvSchema = z.enum(["development", "staging", "production"]);

// 2. Define the schema for expected environment variables
const EnvSchema = z
  .object({
    VITE_API_BASE_URL: z
      .string()
      .trim() // CLEANUP: Handle accidental copy-paste whitespace
      .url("Invalid API Base URL")
      // FIX: Restore default for local dev.
      // Security is still enforced by the .refine() block below which forbids
      // insecure URLs in production.
      .default("http://localhost:8000"),
    VITE_APP_ENV: AppEnvSchema.default("development"),

    // Feature Flags / Dev Tools (Strings in .env, transformed later)
    VITE_USE_ACCOUNT_RECOVERY_MOCKS: z
      .enum(["true", "false"])
      .optional()
      .default("false"),
  })
  // SECURITY: Strictly enforce HTTPS in production to prevent MITM.
  // This ensures that even if the default localhost URL is used,
  // the app will crash in production, preventing insecure deployments.
  .refine(
    (data) => {
      if (data.VITE_APP_ENV === "production") {
        return data.VITE_API_BASE_URL.startsWith("https://");
      }
      return true;
    },
    {
      message: "Production API URL must use HTTPS",
      path: ["VITE_API_BASE_URL"],
    }
  );

// 3. Process and Validate
// We validate strictly against import.meta.env
const parseEnv = () => {
  // Safe parse to capture specific errors
  const parsed = EnvSchema.safeParse({
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_APP_ENV: import.meta.env.VITE_APP_ENV,
    VITE_USE_ACCOUNT_RECOVERY_MOCKS: import.meta.env
      .VITE_USE_ACCOUNT_RECOVERY_MOCKS,
  });

  if (!parsed.success) {
    // CRITICAL: Log specific validation errors to help developers debug
    // FIX: JSON.stringify ensures the actual error message is readable in console
    console.error(
      "❌ Invalid environment configuration:",
      JSON.stringify(parsed.error.flatten().fieldErrors, null, 2)
    );
    // Stop execution to prevent undefined behavior
    throw new Error(
      "Application configuration is invalid. See console for details."
    );
  }

  return parsed.data;
};

const rawEnv = parseEnv();

// 4. Export the structured, type-safe configuration
export const env = {
  apiBaseUrl: rawEnv.VITE_API_BASE_URL,
  appEnv: rawEnv.VITE_APP_ENV,

  // Helper booleans for cleaner logic in components
  isDevelopment: rawEnv.VITE_APP_ENV === "development",
  isStaging: rawEnv.VITE_APP_ENV === "staging",
  isProduction: rawEnv.VITE_APP_ENV === "production",

  // Feature Flags (Converted to boolean for easier usage)
  useAccountRecoveryMocks: rawEnv.VITE_USE_ACCOUNT_RECOVERY_MOCKS === "true",
} as const;

// Export the type inferred from the implementation
export type EnvConfig = typeof env;
